import React, { useState, useEffect, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import { API, graphqlOperation } from "aws-amplify";
import { getMCCTaskDeleteMessagesDetails } from "graphql/queries.js";

// react-bootstrap components
import { Container, Row, Col, Spinner, Card, Table } from "react-bootstrap";
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination";
import { convertBytesToGB } from "utilities/commonUtilities";
import { getTaskStatusLabel } from "utilities/uiMappingUtilities";

function DeleteMessagesTaskDetails() {
  const { cloudTaskId } = useParams();
  const history = useHistory();
  const notify = useContext(NotificationAlertContext);
  const [taskData, setTaskData] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  // Table Config Starts
  const headers = [
    { key: "channel_name", label: "Channel Name" },
    { key: "environment_name", label: "Environment Name" },
    { key: "date_rollup", label: "Date Rollup" },
    { key: "deleted_count", label: "Deleted Count" },
    { key: "deleted_size", label: "Deleted Size" },
  ];
  const columnMapping = {
    "Channel Name": "channel_name",
    "Environment Name": "environment_name",
    "Date Rollup": "date_rollup",
    "Deleted Count": "deleted_count",
    "Deleted Size": "deleted_size",
  };
  // Table Config Ends

  const outcomeOptions = [
    { value: 0, label: "Success" },
    { value: 1, label: "Failure" },
  ];

  const taskType = [
    { value: "resendmessages", label: "Resend Messages" },
    { value: "deletemessages", label: "Delete Messages" },
  ];

  const getTaskTypeLabel = (value) =>
    taskType.find((t) => t.value === value)?.label || value || "-";

  const getOutcomeLabel = (value) =>
    outcomeOptions.find((o) => o.value === value)?.label ?? value ?? "-";

  // Fetch task details using GraphQL
  const fetchDeleteMessagesTaskDetails = async () => {
    try {
      setLoading(true);

      const response = await API.graphql(
        graphqlOperation(getMCCTaskDeleteMessagesDetails, {
          cloud_task_id: cloudTaskId,
        }),
      );

      const result = response?.data?.getMCCTaskDeleteMessagesDetails;

      if (result?.success) {
        const body = result.body;
        setTaskData(body);

        // Set report data from the details array
        if (body?.details && Array.isArray(body.details)) {
          setReportData(body.details);
        } else {
          setReportData([]);
        }

        // Set message from body
        setMessage(body?.message || body?.error || "");

        notify("success", "Task details loaded successfully");
        return true;
      } else {
        throw new Error(result.message || "Failed to fetch task details");
      }
    } catch (error) {
      console.error("Error fetching task details:", error);
      history.push("/admin/mirth-tasks");
      notify("danger", `Failed to load task details: ${error.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      if (cloudTaskId) {
        const isSuccess = await fetchDeleteMessagesTaskDetails();
        if (!isSuccess) {
          notify("danger");
        }
      } else {
        notify("danger", "Missing required parameters for task details");
        history.push("/admin/mirth-tasks");
      }
    }
    fetchData();
  }, [cloudTaskId]);

  if (loading) return <Spinner animation="grow" />;

  return (
    <>
      <Container fluid>
        <Row>
          <Col>
            <h2>Cloud Task - Delete Messages Details</h2>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <Card>
              <Card.Body>
                <Row>
                  <Col md="6">
                    <div id="delete-task-summary">
                      <h5>Task Id: {cloudTaskId}</h5>
                      <h5>Client Name: {taskData?.client_name}</h5>
                      <h5>
                        Environment Name: {taskData?.client_environment_name}
                      </h5>
                      <h5>Status: {getTaskStatusLabel(taskData?.status, "deletemessages")}</h5>
                      <h5>Outcome: {getOutcomeLabel(taskData?.outcome)}</h5>
                      <h5>Message: {message}</h5>
                    </div>
                    <div id="delete-task-success-details">
                      {taskData?.outcome === 0 && (
                        <>
                          <h5>
                            Deleted Size:{" "}
                            {convertBytesToGB(taskData?.deleted_size) !== null
                              ? convertBytesToGB(taskData?.deleted_size) + " GB"
                              : ""}
                          </h5>
                          <h5>Delete Job Id: {taskData?.delete_job_id}</h5>
                          <h5>Deleted Count: {taskData?.deleted_count}</h5>
                          <h5>Total Prefixes: {taskData?.total_prefixes}</h5>
                          <h5>
                            Completed Prefixes: {taskData?.completed_prefixes}
                          </h5>
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            {reportData.length > 0 && (
                <ReactTable
                  id="delete-task-report-table"
                  data={reportData}
                  totalCount={reportData.length}
                  makePaginationStatic={true}
                  hidePagination={true}
                  numberOfRowsData={[]}
                  columnMapping={columnMapping}
                  tableName="Delete Messages Report"
                  CSVFileName={"Delete_Messages_Report_Export"}
                  CSVHeaders={headers}
                  columns={[
                    {
                      Header: "Channel Name",
                      accessor: "channel_name",
                      filterable: false,
                      showTooltip: true,
                      sortable: true,
                    },
                    {
                      Header: "Date Rollup",
                      accessor: "date_rollup",
                      filterable: false,
                      showTooltip: true,
                      sortable: true,
                    },
                  ]}
                />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default DeleteMessagesTaskDetails;
